/**
 * CreateTicketModal Component
 *
 * Modal for creating a new support ticket with subject, description, category, and priority.
 */

import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Select } from '../../../components/ui/Select';
import { Alert } from '../../../components/ui/Alert';
import { useCreateSupportTicket } from '../hooks/useCreateSupportTicket';
import type { SupportTicketCreateDTO } from '../../../types/support-ticket.types';
import { categoryLabels, priorityLabels } from '../../../types/support-ticket.types';
import { getErrorMessage } from '../../../lib/utils/errors';

// Validation schema
const createTicketSchema = z.object({
  subject: z.string().min(5, 'Subject must be at least 5 characters').max(200, 'Subject must be less than 200 characters'),
  category: z.enum(['bug', 'feature_request', 'billing', 'technical_support', 'data_issue', 'other']),
  priority: z.enum(['low', 'medium', 'high', 'critical']),
  description: z.string().min(10, 'Description must be at least 10 characters').max(10000, 'Description must be less than 10,000 characters'),
  applicationId: z.string().optional(),
});

type CreateTicketFormData = z.infer<typeof createTicketSchema>;

interface CreateTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
}

/**
 * CreateTicketModal component
 */
export const CreateTicketModal: React.FC<CreateTicketModalProps> = ({
  isOpen,
  onClose,
  tenantId,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateTicketFormData>({
    resolver: zodResolver(createTicketSchema),
    defaultValues: {
      subject: '',
      category: 'technical_support',
      priority: 'medium',
      description: '',
    },
  });

  const createMutation = useCreateSupportTicket({
    tenantId,
    onSuccess: (data) => {
      handleClose();
    },
    onError: (error) => {
      console.error('Failed to create ticket:', error);
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: CreateTicketFormData) => {
    const submitData: SupportTicketCreateDTO = {
      subject: data.subject,
      category: data.category,
      priority: data.priority,
      description: data.description,
      applicationId: data.applicationId || undefined,
    };
    createMutation.mutate(submitData);
  };

  const isLoading = createMutation.isPending;
  const error = createMutation.error;

  const categoryOptions = Object.entries(categoryLabels).map(([value, label]) => ({
    value,
    label,
  }));

  const priorityOptions = Object.entries(priorityLabels).map(([value, label]) => ({
    value,
    label,
  }));

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>Create Support Ticket</Modal.Header>

      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Error Alert */}
          {error && (
            <Alert variant="error">
              {getErrorMessage(error)}
            </Alert>
          )}

          {/* Subject Field */}
          <div>
            <Input
              label="Subject"
              placeholder="Brief description of your issue"
              {...register('subject')}
              error={errors.subject?.message}
              disabled={isLoading}
              required
            />
          </div>

          {/* Category and Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                {...register('category')}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                disabled={isLoading}
              >
                {categoryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.category && (
                <p className="mt-1 text-sm text-red-600">{errors.category.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority <span className="text-red-500">*</span>
              </label>
              <select
                {...register('priority')}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                disabled={isLoading}
              >
                {priorityOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.priority && (
                <p className="mt-1 text-sm text-red-600">{errors.priority.message}</p>
              )}
            </div>
          </div>

          {/* Description Field */}
          <div>
            <Textarea
              label="Description"
              placeholder="Please describe your issue in detail. Include any error messages, steps to reproduce, and what you expected to happen."
              rows={6}
              {...register('description')}
              error={errors.description?.message}
              disabled={isLoading}
              required
            />
          </div>

          {/* Help text */}
          <div className="text-sm text-gray-500">
            <p>
              Our support team typically responds within 72 business hours (Mon-Fri 9-5 UTC).
              For critical issues, we prioritize faster response times.
            </p>
          </div>
        </form>
      </Modal.Body>

      <Modal.Footer>
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit(onSubmit)}
            isLoading={isLoading}
          >
            Create Ticket
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
